import React, { useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useForm, FormProvider } from 'react-hook-form'
import { memo } from 'react'
import { useTheme, useMediaQuery } from '@mui/material'

import {
  CLIENT_SIDE_CHECKBOX_FIELD_NAME,
  FILTER_MODES,
} from '../../../../../components/shared/table/table.constants'
import { capitalizeFirstLetter } from '@hapana/shared/utils/string.util'
import RadioGroup from '../../../../../components/shared/radio-group/radio-group.component'
import { RADIO_TYPES } from '../../../../../components/shared/radio-button/radio-button.constants'

import { styles } from '../../table.styles'

const RadioGroupFilter = ({
  column: { preFilteredRows, id, filterValue, serverSideFilterOptions },
  setFilter,
  filterMode,
  disabled,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'))

  const options = useMemo(() => {
    const newOptions = []
    if (filterMode === FILTER_MODES.CLIENT_SIDE) {
      preFilteredRows.forEach((row) => {
        if (newOptions.find((option) => option.value === row.values[id])) return
        newOptions.push({
          value: row.values[id],
          label: capitalizeFirstLetter(row.values[id]),
        })
      })
      return newOptions
    } else {
      return serverSideFilterOptions || []
    }
  }, [id, preFilteredRows, filterMode, serverSideFilterOptions])

  const fieldName = `${CLIENT_SIDE_CHECKBOX_FIELD_NAME}-${id}`

  const formMethods = useForm({
    defaultValues: {
      [fieldName]: filterValue || [],
    },
  })

  const filteredValue = formMethods.watch(fieldName)

  useEffect(() => {
    setFilter(id, filteredValue || filteredValue === false ? filteredValue : undefined)
  }, [filteredValue, setFilter, id])

  return (
    <FormProvider {...formMethods}>
      <form
        style={isMobile ? styles.tableFormMobile : styles.tableForm({ palette: theme.palette })}
      >
        <RadioGroup
          radioType={RADIO_TYPES.LABEL}
          name={fieldName}
          options={options}
          radioGroupStyles={styles.optionsRadio}
          disabled={disabled}
          row={true}
        />
      </form>
    </FormProvider>
  )
}

RadioGroupFilter.propTypes = {
  column: PropTypes.shape({
    filterValue: PropTypes.array,
    preFilteredRows: PropTypes.array,
    id: PropTypes.string,
    serverSideFilterOptions: PropTypes.array,
  }).isRequired,
  setFilter: PropTypes.func.isRequired,
  filterMode: PropTypes.oneOf(Object.values(FILTER_MODES)),
  disabled: PropTypes.bool,
}

function areEqual(prevProps, nextProps) {
  return (
    prevProps.column.serverSideFilterOptions === nextProps.column.serverSideFilterOptions &&
    prevProps.column.filterValue === nextProps.column.filterValue
  )
}

export default memo(RadioGroupFilter, areEqual)
